import React, { memo, useMemo, useRef, useEffect } from "react";
import XGSIcon from "ui-components/icon/xgsIcon";
import XGSIcons from "ui-components/icon/xgsIcons";
import XGSCheckbox from "ui-components/xgs-checkbox/xgsCheckbox";
import {
  DEFAULT_DOC_SELECTION,
  ProbillDocSelection,
  ProbillSelectionMap,
  formatManifestDisplay,
  formatProbillDisplay,
} from "./types";

export interface DocumentSelectionTableProps {
  validProbills: number[];
  deliveryManifestByProbill: Record<number, number | undefined>;
  selection: ProbillSelectionMap;
  onSelectionChange: (selection: ProbillSelectionMap) => void;
}

const DocumentSelectionTable: React.FC<DocumentSelectionTableProps> = memo((props) => {
  const { validProbills, deliveryManifestByProbill, selection, onSelectionChange } = props;
  const headerProbillRef = useRef<HTMLInputElement>(null);
  const headerDriverRef = useRef<HTMLInputElement>(null);
  const headerLabelRef = useRef<HTMLInputElement>(null);
  const headerManifestRef = useRef<HTMLInputElement>(null);

  const probillsWithManifest = useMemo(
    () => validProbills.filter((p) => !!deliveryManifestByProbill[p]),
    [validProbills, deliveryManifestByProbill]
  );

  const stats = useMemo(() => {
    let driverCount = 0;
    let labelCount = 0;
    let manifestCount = 0;
    let rowsFullySelected = 0;
    let rowsPartiallySelected = 0;

    for (const probill of validProbills) {
      const row = selection[probill] || DEFAULT_DOC_SELECTION;
      const hasManifest = !!deliveryManifestByProbill[probill];
      if (row.driverReceipt) driverCount += 1;
      if (row.shippingLabel) labelCount += 1;
      if (hasManifest && row.deliveryManifest) manifestCount += 1;

      const selectableCount = hasManifest ? 3 : 2;
      const selectedCount =
        (row.driverReceipt ? 1 : 0) +
        (row.shippingLabel ? 1 : 0) +
        (hasManifest && row.deliveryManifest ? 1 : 0);

      if (selectedCount === selectableCount && selectedCount > 0) rowsFullySelected += 1;
      else if (selectedCount > 0) rowsPartiallySelected += 1;
    }

    const uniqueSelectedManifests = new Set<number>();
    for (const probill of validProbills) {
      const mf = deliveryManifestByProbill[probill];
      if (mf && selection[probill]?.deliveryManifest) {
        uniqueSelectedManifests.add(mf);
      }
    }

    const totalDocs = driverCount + labelCount + uniqueSelectedManifests.size;
    const allSelected =
      validProbills.length > 0 &&
      driverCount === validProbills.length &&
      labelCount === validProbills.length &&
      (probillsWithManifest.length === 0 ||
        manifestCount === probillsWithManifest.length);

    return {
      driverCount,
      labelCount,
      manifestCount,
      uniqueManifestCount: uniqueSelectedManifests.size,
      totalDocs,
      rowsFullySelected,
      rowsPartiallySelected,
      allSelected,
      driverAll: validProbills.length > 0 && driverCount === validProbills.length,
      driverSome: driverCount > 0 && driverCount < validProbills.length,
      labelAll: validProbills.length > 0 && labelCount === validProbills.length,
      labelSome: labelCount > 0 && labelCount < validProbills.length,
      manifestAll:
        probillsWithManifest.length > 0 &&
        manifestCount === probillsWithManifest.length,
      manifestSome:
        manifestCount > 0 && manifestCount < probillsWithManifest.length,
    };
  }, [validProbills, selection, deliveryManifestByProbill, probillsWithManifest.length]);

  useEffect(() => {
    if (headerProbillRef.current) {
      headerProbillRef.current.indeterminate =
        stats.rowsPartiallySelected > 0 ||
        (stats.rowsFullySelected > 0 && stats.rowsFullySelected < validProbills.length);
    }
    if (headerDriverRef.current) {
      headerDriverRef.current.indeterminate = stats.driverSome;
    }
    if (headerLabelRef.current) {
      headerLabelRef.current.indeterminate = stats.labelSome;
    }
    if (headerManifestRef.current) {
      headerManifestRef.current.indeterminate = stats.manifestSome;
    }
  }, [stats, validProbills.length]);

  const getRow = (probill: number): ProbillDocSelection =>
    selection[probill] || DEFAULT_DOC_SELECTION;

  const updateRow = (probill: number, patch: Partial<ProbillDocSelection>) => {
    const current = getRow(probill);
    onSelectionChange({
      ...selection,
      [probill]: { ...current, ...patch },
    });
  };

  const toggleRowBoth = (probill: number, checked: boolean) => {
    const hasManifest = !!deliveryManifestByProbill[probill];
    updateRow(probill, {
      driverReceipt: checked,
      shippingLabel: checked,
      deliveryManifest: hasManifest ? checked : false,
    });
  };

  const toggleHeaderProbill = (checked: boolean) => {
    const next: ProbillSelectionMap = {};
    for (const probill of validProbills) {
      const hasManifest = !!deliveryManifestByProbill[probill];
      next[probill] = {
        driverReceipt: checked,
        shippingLabel: checked,
        deliveryManifest: hasManifest ? checked : false,
      };
    }
    onSelectionChange(next);
  };

  const toggleColumn = (
    type: "driverReceipt" | "shippingLabel" | "deliveryManifest",
    checked: boolean
  ) => {
    const next: ProbillSelectionMap = {};
    for (const probill of validProbills) {
      const current = getRow(probill);
      if (type === "deliveryManifest" && !deliveryManifestByProbill[probill]) {
        next[probill] = { ...current, deliveryManifest: false };
      } else {
        next[probill] = { ...current, [type]: checked };
      }
    }
    onSelectionChange(next);
  };

  const columnStatusLabel = (all: boolean, some: boolean, count: number) => {
    if (all) return `✓ All ${count} selected`;
    if (some) return "– Some selected";
    return null;
  };

  const driverProbills = validProbills.filter((p) => selection[p]?.driverReceipt);
  const labelProbills = validProbills.filter((p) => selection[p]?.shippingLabel);
  const selectedManifestDisplays = useMemo(() => {
    const seen = new Set<number>();
    const values: number[] = [];
    for (const probill of validProbills) {
      const mf = deliveryManifestByProbill[probill];
      if (mf && selection[probill]?.deliveryManifest && !seen.has(mf)) {
        seen.add(mf);
        values.push(mf);
      }
    }
    return values;
  }, [validProbills, deliveryManifestByProbill, selection]);

  return (
    <div className="xgs-print-download__matrix">
      <div className="xgs-print-download__matrix__table">
        <div className="xgs-print-download__matrix__header">
          <div className="xgs-print-download__matrix__col xgs-print-download__matrix__col--probill">
            <XGSCheckbox
              name="header-probill-select"
              checked={stats.allSelected}
              onChange={(e) => toggleHeaderProbill(e.target.checked)}
              ref={headerProbillRef}
            />
            <span className="xgs-print-download__matrix__col-label">Probill</span>
          </div>
          <div className="xgs-print-download__matrix__col xgs-print-download__matrix__col--doc">
            <div className="xgs-print-download__matrix__doc-title">
              <XGSCheckbox
                name="header-driver-select"
                checked={stats.driverAll}
                onChange={(e) => toggleColumn("driverReceipt", e.target.checked)}
                ref={headerDriverRef}
                mix="xgs-print-download__checkbox--doc"
              />
              <XGSIcon icon={XGSIcons.faFileAlt} className="xgs-print-download__matrix__doc-icon" />
              <div className="xgs-print-download__matrix__doc-name">Driver Receipts</div>
            </div>
            {columnStatusLabel(stats.driverAll, stats.driverSome, validProbills.length) && (
              <span className="xgs-print-download__status-pill">
                {columnStatusLabel(stats.driverAll, stats.driverSome, validProbills.length)}
              </span>
            )}
          </div>
          <div className="xgs-print-download__matrix__col xgs-print-download__matrix__col--doc">
            <div className="xgs-print-download__matrix__doc-title">
              <XGSCheckbox
                name="header-label-select"
                checked={stats.labelAll}
                onChange={(e) => toggleColumn("shippingLabel", e.target.checked)}
                ref={headerLabelRef}
                mix="xgs-print-download__checkbox--doc"
              />
              <XGSIcon icon={XGSIcons.faBarcode} className="xgs-print-download__matrix__doc-icon" />
              <div className="xgs-print-download__matrix__doc-name">Shipment Labels</div>
            </div>
            {columnStatusLabel(stats.labelAll, stats.labelSome, validProbills.length) && (
              <span className="xgs-print-download__status-pill">
                {columnStatusLabel(stats.labelAll, stats.labelSome, validProbills.length)}
              </span>
            )}
          </div>
          <div className="xgs-print-download__matrix__col xgs-print-download__matrix__col--doc">
            <div className="xgs-print-download__matrix__doc-title">
              <XGSCheckbox
                name="header-manifest-select"
                checked={stats.manifestAll}
                onChange={(e) => toggleColumn("deliveryManifest", e.target.checked)}
                ref={headerManifestRef}
                mix="xgs-print-download__checkbox--doc"
                disabled={probillsWithManifest.length === 0}
              />
              <XGSIcon icon={XGSIcons.faClipboardList} className="xgs-print-download__matrix__doc-icon" />
              <div className="xgs-print-download__matrix__doc-name">Delivery Manifest</div>
            </div>
            {columnStatusLabel(
              stats.manifestAll,
              stats.manifestSome,
              probillsWithManifest.length
            ) && (
              <span className="xgs-print-download__status-pill">
                {columnStatusLabel(
                  stats.manifestAll,
                  stats.manifestSome,
                  probillsWithManifest.length
                )}
              </span>
            )}
          </div>
        </div>

        {validProbills.map((probill) => {
          const row = getRow(probill);
          const deliveryManifestNumber = deliveryManifestByProbill[probill];
          const hasManifest = !!deliveryManifestNumber;
          const selectableCount = hasManifest ? 3 : 2;
          const selectedCount =
            (row.driverReceipt ? 1 : 0) +
            (row.shippingLabel ? 1 : 0) +
            (hasManifest && row.deliveryManifest ? 1 : 0);
          const both = selectedCount === selectableCount && selectedCount > 0;
          const partial = selectedCount > 0 && selectedCount < selectableCount;

          return (
            <MatrixRow
              key={probill}
              probill={probill}
              row={row}
              both={both}
              partial={partial}
              deliveryManifestNumber={deliveryManifestNumber}
              onToggleBoth={(checked) => toggleRowBoth(probill, checked)}
              onToggleDriver={(checked) => updateRow(probill, { driverReceipt: checked })}
              onToggleLabel={(checked) => updateRow(probill, { shippingLabel: checked })}
              onToggleManifest={(checked) =>
                updateRow(probill, { deliveryManifest: checked })
              }
            />
          );
        })}
      </div>

      {stats.totalDocs > 0 && (
        <div className="xgs-print-download__summary">
          <div className="xgs-print-download__summary__title">
            {stats.totalDocs} document{stats.totalDocs === 1 ? "" : "s"} selected
          </div>
          <div className="xgs-print-download__summary__rows">
            {driverProbills.length > 0 && (
              <div className="xgs-print-download__summary__row">
                <span className="xgs-print-download__summary__tag">Driver Receipt</span>
                <span className="xgs-print-download__summary__values">
                  {driverProbills.map(formatProbillDisplay).join(", ")}
                </span>
              </div>
            )}
            {labelProbills.length > 0 && (
              <div className="xgs-print-download__summary__row">
                <span className="xgs-print-download__summary__tag">Shipment Label</span>
                <span className="xgs-print-download__summary__values">
                  {labelProbills.map(formatProbillDisplay).join(", ")}
                </span>
              </div>
            )}
            {selectedManifestDisplays.length > 0 && (
              <div className="xgs-print-download__summary__row">
                <span className="xgs-print-download__summary__tag">Delivery Manifest</span>
                <span className="xgs-print-download__summary__values">
                  {selectedManifestDisplays.map(formatManifestDisplay).join(", ")}
                </span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
});

interface MatrixRowProps {
  probill: number;
  row: ProbillDocSelection;
  both: boolean;
  partial: boolean;
  deliveryManifestNumber?: number;
  onToggleBoth: (checked: boolean) => void;
  onToggleDriver: (checked: boolean) => void;
  onToggleLabel: (checked: boolean) => void;
  onToggleManifest: (checked: boolean) => void;
}

const MatrixRow: React.FC<MatrixRowProps> = memo((props) => {
  const {
    probill,
    row,
    both,
    partial,
    deliveryManifestNumber,
    onToggleBoth,
    onToggleDriver,
    onToggleLabel,
    onToggleManifest,
  } = props;
  const rowRef = useRef<HTMLInputElement>(null);
  const hasManifest = !!deliveryManifestNumber;

  useEffect(() => {
    if (rowRef.current) {
      rowRef.current.indeterminate = partial;
    }
  }, [partial]);

  return (
    <div className="xgs-print-download__matrix__row">
      <div className="xgs-print-download__matrix__col xgs-print-download__matrix__col--probill">
        <XGSCheckbox
          name={`row-${probill}`}
          checked={both}
          onChange={(e) => onToggleBoth(e.target.checked)}
          ref={rowRef}
        />
        <div>
          <div className="xgs-print-download__matrix__probill-id">{formatProbillDisplay(probill)}</div>
          <div className="xgs-print-download__matrix__verified">Verified ✓</div>
        </div>
      </div>
      <div className="xgs-print-download__matrix__col xgs-print-download__matrix__col--center">
        <XGSCheckbox
          name={`driver-${probill}`}
          checked={row.driverReceipt}
          onChange={(e) => onToggleDriver(e.target.checked)}
          mix="xgs-print-download__checkbox--doc"
        />
      </div>
      <div className="xgs-print-download__matrix__col xgs-print-download__matrix__col--center">
        <XGSCheckbox
          name={`label-${probill}`}
          checked={row.shippingLabel}
          onChange={(e) => onToggleLabel(e.target.checked)}
          mix="xgs-print-download__checkbox--doc"
        />
      </div>
      <div
        className={`xgs-print-download__matrix__col xgs-print-download__matrix__col--manifest${
          hasManifest ? "" : " xgs-print-download__matrix__col--disabled"
        }`}
      >
        <XGSCheckbox
          name={`manifest-${probill}`}
          checked={hasManifest && row.deliveryManifest}
          onChange={(e) => onToggleManifest(e.target.checked)}
          mix="xgs-print-download__checkbox--doc"
          disabled={!hasManifest}
        />
        {hasManifest ? (
          <span className="xgs-print-download__matrix__manifest-id">
            {formatManifestDisplay(deliveryManifestNumber as number)}
          </span>
        ) : (
          <span className="xgs-print-download__matrix__manifest-missing">No manifest</span>
        )}
      </div>
    </div>
  );
});

export default DocumentSelectionTable;
